export function computeThumbSize(
  w: number,
  h: number,
  maxDim = 512,
): { width: number; height: number } {
  if (w <= 0 || h <= 0) return { width: 0, height: 0 };
  const dim = Math.max(0, maxDim);
  const scale = Math.min(1, dim / Math.max(w, h));
  return { width: Math.round(w * scale), height: Math.round(h * scale) };
}

interface ThumbDeps {
  maxDim?: number;
  quality?: number;
  createBitmap?: (bytes: Blob, options: ImageBitmapOptions) => Promise<ImageBitmap>;
  makeCanvas?: (w: number, h: number) => OffscreenCanvas;
  toBlob?: (canvas: OffscreenCanvas) => Promise<Blob | null>;
}

export async function makeThumbnail(bytes: Blob, deps: ThumbDeps = {}): Promise<Blob> {
  const createBitmap = deps.createBitmap ?? createImageBitmap;
  // The encode drops the EXIF Orientation tag, so the pixels must be upright before drawing.
  const bitmap = await createBitmap(bytes, { imageOrientation: 'from-image' });
  const makeCanvas = deps.makeCanvas ?? ((w, h) => new OffscreenCanvas(w, h));
  let canvas: OffscreenCanvas;
  try {
    const { width, height } = computeThumbSize(bitmap.width, bitmap.height, deps.maxDim);
    canvas = makeCanvas(width, height);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('makeThumbnail: 2d context unavailable');
    ctx.drawImage(bitmap, 0, 0, width, height);
  } finally {
    bitmap.close();
  }
  const quality = deps.quality ?? 0.8;
  const toBlob = deps.toBlob ?? ((c) => c.convertToBlob({ type: 'image/jpeg', quality }));
  const out = await toBlob(canvas);
  if (!out) throw new Error('makeThumbnail: encode failed');
  return out;
}
